import type { DocumentDto, DocumentPartDto, DocumentScope, DocumentSheetDto } from '@dbrain/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, upload } from './client.js';
import { planKeys } from './plan.js';

/**
 * Documents (Phase 12; DOC-001…DOC-014): the library, a conversation's
 * documents, previews. Lists refresh every few seconds while something is
 * still being read, so the status and the size estimate appear by themselves.
 */
export const docKeys = {
  all: ['documents'] as const,
  list: (scope: string, q: string) => ['documents', 'list', scope, q] as const,
  one: (id: string) => ['documents', 'one', id] as const,
  conversation: (conversationId: string) => ['documents', 'conversation', conversationId] as const,
  sheets: (id: string) => ['documents', 'sheets', id] as const,
  part: (id: string, number: number) => ['documents', 'part', id, number] as const,
};

const busy = (docs: DocumentDto[] | undefined) => (docs ?? []).some((d) => d.status === 'queued' || d.status === 'processing');

export function useDocuments(scope: DocumentScope | 'all', q: string) {
  return useQuery({
    queryKey: docKeys.list(scope, q),
    queryFn: () => {
      const params = new URLSearchParams({ limit: '200' });
      if (scope !== 'all') params.set('scope', scope);
      if (q.trim()) params.set('q', q.trim());
      return api<DocumentDto[]>('GET', `/api/documents?${params.toString()}`);
    },
    refetchInterval: (query) => (busy(query.state.data) ? 2_000 : false),
  });
}

export function useConversationDocuments(conversationId: string, enabled = true) {
  return useQuery({
    queryKey: docKeys.conversation(conversationId),
    queryFn: () => api<DocumentDto[]>('GET', `/api/conversations/${conversationId}/documents`),
    enabled: enabled && Boolean(conversationId),
    refetchInterval: (query) => (busy(query.state.data) ? 2_000 : false),
  });
}

export function useDocument(id: string | null) {
  return useQuery({
    queryKey: docKeys.one(id ?? ''),
    queryFn: () => api<DocumentDto>('GET', `/api/documents/${id}`),
    enabled: Boolean(id),
    refetchInterval: (query) => (query.state.data && busy([query.state.data]) ? 2_000 : false),
  });
}

export function useSheets(id: string, enabled: boolean) {
  return useQuery({ queryKey: docKeys.sheets(id), queryFn: () => api<DocumentSheetDto[]>('GET', `/api/documents/${id}/sheets`), enabled, staleTime: 60_000 });
}

export function usePart(id: string, number: number, enabled: boolean) {
  return useQuery({
    queryKey: docKeys.part(id, number),
    queryFn: () => api<DocumentPartDto[]>('GET', `/api/documents/${id}/parts?from=${number}&to=${number}`),
    enabled,
    staleTime: 60_000,
  });
}

export function uploadDocument(file: File, query: Record<string, string>): Promise<DocumentDto> {
  return upload<DocumentDto>('POST', `/api/documents?${new URLSearchParams(query).toString()}`, file, { 'x-file-name': encodeURIComponent(file.name) });
}

/** Refreshes every document list after a change. */
export function useRefreshDocuments(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: docKeys.all });
    // Phase 16a: the documents a plan counts
    void queryClient.invalidateQueries({ queryKey: planKeys.usage });
  };
}
